import { ecorEnv } from '@/../environments/environment';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { AppSettings, AppSettingsService } from '@/common/services/app-settings.service';
import { FilingStatusDialogData } from '@/surveys/dialogs-data/filing-status-dialog-data';
import { FilingStatusDialogService } from '@/surveys/services/filing-status-dialog.service';
import { RequestExtensionDialogData } from '@/surveys/dialogs-data/request-extension-dialog-data';
import { RequestExtensionDialogService } from '@/surveys/services/request-extension-dialog.service';
import { ShareSurveyAccessDialogData } from '@/surveys/dialogs-data/share-survey-access-dialog-data';
import { ShareSurveyAccessDialogService } from '@/surveys/services/share-survey-access-dialog.service';
import { EventService } from '@/surveys/services/event.service';
import { SurveyCard } from '@/surveys/states/surveys/survey-card';
import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionStorageService } from 'ngx-webstorage';
import { Subscription } from 'rxjs';
import { SurveyViewsInstanceService } from '@/surveys/services/survey-views-instance.service';
import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { WINDOW } from '@/window';

@Component({
    selector: 'app-survey-views-instance-base',
    template: '<div>survey-views-instance-base works!</div>',
    standalone: true,
    providers: [
        ParadataEventService,
        FilingStatusDialogService,
        RequestExtensionDialogService,
        ShareSurveyAccessDialogService,
        EventService,
        SessionStorageService,
        SurveyViewsInstanceService
    ]
})
export class SurveyViewsInstanceBaseComponent implements OnInit {
    options = false;
    channel$: Subscription;
    requestExtension$: Subscription;
    shareSurveyAccess$: Subscription;
    filingStatus$: Subscription;
    post$: Subscription;
    appSettings$: Subscription;
    loading = false;
    showFaqChildren = false;
    @ViewChild('submitButton')
    submitButton: ElementRef;
    showOnlyMessage = false;

    settings: AppSettings = { 
        pegaIntegration: true
    };

    /** Magic info */
    info = false;

    @Input('survey')
    survey: SurveyCard;

    @Input('active')
    active: boolean;

    accountManagerTooltipHtml = `
    An Account Manager will
    serve as your Census point of
    contact for all questions and
    concerns regarding your
    survey(s).`;

    constructor(public router: Router,
        public route: ActivatedRoute,
        public requestExtensionDialogService: RequestExtensionDialogService,
        public shareSurveyAccessDialogService: ShareSurveyAccessDialogService,
        public paradataEventService: ParadataEventService,
        public filingStatusDialogService: FilingStatusDialogService,
        public surveyViewsInstanceService: SurveyViewsInstanceService,
        public eventService: EventService,
        public appSettingsService: AppSettingsService,
        public sessionStorageService: SessionStorageService,
        @Inject(WINDOW) public window: Window
    ) { 
            this.appSettings$ = this.appSettingsService.getAppSettings().subscribe( settings => {
                if ( typeof settings === 'string' ) {
                      // Manually retyping to string then removing tainting and reparsing
                    this.settings = JSON.parse( JSON.stringify( settings ).slice( 8, -1 ).replace( /\\/g, '' ) );
                }
            });
        }

    ngOnInit() {
        if (!this.survey) {
            console.error('Survey is undefined in SurveyViewsInstanceBaseComponent');
            return;
        }
        
        this.showOnlyMessage = this.survey.preCanvasFlag;
        this.surveyAdded();
    }

    surveyAdded() {
        if (this.survey.justAdded) {
            this.optionsClick();
        }
    }

    sendMessage() {
        this.paradataEventService.add(ParadataEventEnum.Message, this.survey.surveyId);

        // this.router.navigate(['/submit-case']);

        /**
         * TODO: Removed after IE is no longer supported, and user the reserved block below it.
         */
        // const url = '/submit-case?surveyInstanceId=' + this.survey.surveyInstanceId + '&descr=' + this.survey.descr
        //     + '&surveyId=' + this.survey.surveyId + '&abbrFull=' + this.survey.abbrFull;
        // this.router.navigateByUrl(url);

        /**
         * TODO: Reserved to use after IE is no longer supported.
         */
        this.router.navigate(['/submit-case'], {
            relativeTo: this.route,
            replaceUrl: true,
            queryParams:
            {
                surveyInstanceId: this.survey.surveyInstanceId,
                brOrganizationId: this.survey.brOrganizationId,
                descr: this.survey.descr,
                surveyId: this.survey.surveyId,
                abbrFull: this.survey.abbrFull
            }
        });
    }

    accessMessages() {    
        this.router.navigate(['/case-list'], {
            relativeTo: this.route,
            replaceUrl: true
        });
    }

    infoClick() {
        this.info = !this.info;
        this.options = false;
        this.paradataEventService.add(ParadataEventEnum.Information, this.survey.surveyId);
    }

    requestExtension() {
        if (!this.survey) {
            console.error('Survey is undefined, cannot open request extension dialog');
            console.trace('RequestExtension called with undefined survey');
            return;
        }
    
        const options: RequestExtensionDialogData = {
            title: 'SURVEY EXTENSION REQUEST',
            cancelButtonLabel: 'Cancel Request',
            confirmButtonLabel: 'Submit Request',
            survey: this.survey
        };

        this.requestExtensionDialogService.open(options);
        this.paradataEventService.add(ParadataEventEnum.TimeExtension, this.survey.surveyId);

        this.requestExtension$ = this.requestExtensionDialogService.confirmed().subscribe(res => {
            if (res === 'success') {
                this.router.navigate(['/surveys']);
            }
        });
    }

    shareAccess() {
        if (!this.survey) {
            console.error('Survey is undefined, cannot open share survey access dialog');
            console.trace('ShareAccess called with undefined survey');
            return;
        }

        const options: ShareSurveyAccessDialogData = {
            title: 'SHARE MY SURVEY',
            cancelButtonLabel: 'Cancel Request',
            confirmButtonLabel: 'Submit Request',
            survey: this.survey
        };

        this.shareSurveyAccessDialogService.open(options);
        this.paradataEventService.add(ParadataEventEnum.ShareSurveyAccess, this.survey.surveyId);

        this.shareSurveyAccess$ = this.shareSurveyAccessDialogService.confirmed().subscribe(
            res => {
                this.paradataEventService.add(ParadataEventEnum.ShareSurveySubmitSuccess, this.survey.surveyId);
            },
            (error) => {
                this.paradataEventService.add(ParadataEventEnum.ShareSurveySubmitFail, this.survey.surveyId);
            }
        );
    }

    /**
     * @param index TODO: (-1) is for no param
     */
    viewFAQs(idx: number) {
        this.paradataEventService.add(ParadataEventEnum.FAQs, this.survey.surveyId);
        // Record paradata event if necessary, open known FAQ in new tab
        if (this.survey.faqUrl && !this.survey.faqUrl.includes(';')) {
            window.open(this.survey.faqUrl, '_blank');
        } else if (this.survey.faqUrl && this.survey.faqUrl.includes(';')) {
            if (idx !== -1) {
                if (idx === 0) {
                    this.paradataEventService.add(ParadataEventEnum.COSFAQs, this.survey.surveyId);
                } else {
                    this.paradataEventService.add(ParadataEventEnum.ASMFAQs, this.survey.surveyId);
                }

                window.open(this.survey.faqUrl.split(';')[idx], '_blank');
            } else {
                this.showFaqChildren = !this.showFaqChildren;
            }
        } else {
            window.open(ecorEnv.faqUrl + '?pyActivity=pyMobileSnapStart&Action=showHarness&Purpose=KMHelpSitePortal&className=Data-Portal&ReadOnly=true&pzMobileInitActivity=ExternalTaxonomyWrapper&pzMobileContextPageName=pyDisplayHarness&pzMobileInitActivityParams=%26TopicName%3D' + encodeURI(this.survey.descr).replace(/\(/g, '%28').replace(/\)/g, '%29'), '_blank');
        }
    }

    checkStatus() {
        if (!this.survey) {
            console.error('Survey is undefined, cannot check filing status');
            console.trace('CheckStatus called with undefined survey');
            return;
        }
        
        if (this.survey.surveyCategory === 'MEPS') {
            this.submitButton.nativeElement.click();
        } else {
            const options: FilingStatusDialogData = {
                title: 'FILING STATUS',
                cancelButtonLabel: 'Close',
                confirmButtonLabel: 'Export',
                survey: this.survey
            };

            this.paradataEventService.add(ParadataEventEnum.FilingStatus, this.survey.surveyId);
            this.filingStatusDialogService.open(options);

            this.filingStatus$ = this.filingStatusDialogService.confirmed().subscribe(res => { });
        }
    }

    /**
     * @param index TODO: -1 is for no param
     */
    moreInfoClick(index: number) {
        this.paradataEventService.add(ParadataEventEnum.MoreInformation, this.survey.surveyId);
        if (index === -1) {
            window.open(this.survey.infoUrl, '_blank');
        } else {
            window.open(this.survey.infoUrl.split(';')[index], '_blank');
        }
    }

    optionsClick() {
        this.paradataEventService.add(ParadataEventEnum.Options, this.survey.surveyId);
        this.options = !this.options;
        this.info = false;
    }

    /**
     * @param index TODO: (-1, -1) is for no param
     * @param index TODO: (surveyInstanceId, -1) is for no param
     */
    // accessCenturion(surveyInstanceId: number, userAccountId: number) {
    //     this.loading = true;
    //     this.paradataEventService.add(ParadataEventEnum.ReportNow, this.survey.surveyId);
    //     this.channel$ = this.surveyViewsInstanceService.accessCenturion(surveyInstanceId, userAccountId).subscribe(
    //         (data) => {
    //             this.loading = false;
    //         },
    //         (error: any) => {
    //             this.loading = false;
    //         }
    //     );
    // }


    accessCenturion(survey: SurveyCard, userAccountId: number) {
        this.loading = true;
        this.paradataEventService.add(ParadataEventEnum.ReportNow, this.survey.surveyId);
        if (survey.caseId && survey.caseId > 0) { // if caseId exists, use Centurion 2.0 (incoming Mojo surveys should use Centurion 2.0)
            this.accessCenturionV2(survey, userAccountId);
        } else { // if no caseId, use Centurion 1.0 (legacy survey load - did not come in via Mojo)
            this.accessCenturionV1(survey.surveyInstanceId, userAccountId);
             
        }
    }
    /**
     * @param index TODO: (-1, -1) is for no param
     * @param index TODO: (surveyInstanceId, -1) is for no param
     */
    accessCenturionV1(surveyInstanceId: number, userAccountId: number) {
        this.channel$ = this.surveyViewsInstanceService.accessCenturionV1(surveyInstanceId, userAccountId).subscribe(
            (data) => {
                this.loading = false;
            },
            (error: any) => {
                this.loading = false;
            }
        );
    }

    /**
     *  Handle Centurion 2.0 survey and get a JWT.
     */
    accessCenturionV2(survey: SurveyCard, userAccountId: number) {
        this.channel$ = this.surveyViewsInstanceService.accessCenturionV2(survey, userAccountId).subscribe(
            (data) => {
                this.loading = false;
                if (data.jwt) {
                    window.open(`${survey.centurionUrl}?census_auth=${data.jwt}`, "_blank");
                }
            },
            (error: any) => {
                this.loading = false;
            }
        );
    }




    destroy() {
        if (this.channel$) {
            this.channel$.unsubscribe();
        }
        if (this.shareSurveyAccess$) {
            this.shareSurveyAccess$.unsubscribe();
        }
        if (this.requestExtension$) {
            this.requestExtension$.unsubscribe();
        }
        if (this.filingStatus$) {
            this.filingStatus$.unsubscribe();
        }
        if (this.post$) {
            this.post$.unsubscribe();
        }
        if (this.appSettings$) {
            this.appSettings$.unsubscribe();
        }
    }

    openSurveyReportNow(survey: SurveyCard, user: PortalUser) {
        const queryParams = {
            surveyInstanceId: survey.surveyInstanceId,
            caseId: survey.caseId,
            userAccountId: user.userAccountId,
            centurionUrl: survey.centurionUrl

        };
        localStorage.setItem('caseId', `${survey.caseId}`);
        const url = this.router.serializeUrl(
            this.router.createUrlTree(['/survey-report-now'], { queryParams })
        );
        this.window.open(url, '_blank');
    }
}
